import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/hooks/useData';
import { useAuth } from '@/contexts/AuthContext';
import { resolveCardTheme } from '@/lib/cardThemes';
import './SidebarAccountSwitcher.css';

const SWAP_MS = 720;

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

export default function SidebarAccountSwitcher() {
  const { workspace, list, switchWorkspace } = useWorkspace();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [swappingId, setSwappingId] = useState(null);
  const swapTimerRef = useRef(null);

  useEffect(() => () => {
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
  }, []);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const orderedAccounts = useMemo(() => {
    if (!workspace) return list;
    return [workspace, ...list.filter((w) => w.id !== workspace.id)];
  }, [list, workspace]);

  const handleSelect = useCallback((id) => {
    if (!open) { setOpen(true); return; }
    if (swappingId) return;
    if (id === workspace?.id) { setOpen(false); return; }
    // Swap animation 720ms — karty CELE viditelne. Wallet ostane open (no
    // auto-close), backdrop/outside click zatvori smoothne. Ziadny post-jump.
    setSwappingId(id);
    swapTimerRef.current = setTimeout(() => {
      switchWorkspace(id);
      setSwappingId(null);
      swapTimerRef.current = null;
    }, SWAP_MS);
  }, [open, workspace, switchWorkspace, swappingId]);

  const goSettings = useCallback((e) => {
    e.stopPropagation();
    setOpen(false);
    navigate('/nastavenia');
  }, [navigate]);

  const handleSignOut = useCallback((e) => {
    e.stopPropagation();
    setOpen(false);
    signOut();
  }, [signOut]);

  if (!workspace || list.length === 0) return null;

  return (
    <div ref={rootRef} className={`sas-root ${open ? 'is-open' : ''}`}>
      {orderedAccounts.map((ws, i) => {
        const isActive = ws.id === workspace.id;
        // Stable color — derived from ws.id position in original `list` (not orderedAccounts).
        // Inak by sa pri prepnutí workspace zamenili farby (active first reordering).
        const stableIndex = list.findIndex((w) => w.id === ws.id);
        const theme = resolveCardTheme(ws?.settings?.card_color, stableIndex);
        const onKey = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSelect(ws.id);
          }
        };
        const isSwapIn = swappingId === ws.id;
        const isSwapOut = swappingId !== null && isActive && !isSwapIn;
        return (
          <div
            key={ws.id}
            role="button"
            tabIndex={0}
            className={`sas-card ${isActive ? 'is-active' : ''} ${isSwapIn ? 'is-swap-in' : ''} ${isSwapOut ? 'is-swap-out' : ''}`}
            style={{
              '--i': i,
              '--sas-c1': theme.c1,
              '--sas-c2': theme.c2,
            }}
            onClick={() => handleSelect(ws.id)}
            onKeyDown={onKey}
            aria-label={`Prepnúť na ${ws.name}`}
          >
            <span className="sas-grab" aria-hidden="true" />
            <div className="sas-body">
              <div className="sas-row">
                <div className="sas-av">{initials(ws.name)}</div>
                <div className="sas-info">
                  <div className="sas-name">{ws.name}</div>
                  <div className="sas-sub">
                    <b>{ws.type === 'sro' ? 's.r.o.' : 'SZČO'}</b>
                    {ws.ico ? <> · IČO {ws.ico}</> : null}
                  </div>
                </div>
              </div>
              {isActive && (
                <div className="sas-actions">
                  <button type="button" className="sas-ibtn" onClick={goSettings} aria-label="Nastavenia">
                    <svg viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
                    </svg>
                  </button>
                  <button type="button" className="sas-ibtn" onClick={handleSignOut} aria-label="Odhlásiť sa">
                    <svg viewBox="0 0 24 24">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16,17 21,12 16,7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

    </div>
  );
}
